import {
  Collapse,
  type FormListFieldData,
  type FormListOperation,
  Table
} from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React, {
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"

import type {
  DocumentFormData,
  DocumentScopeSectionPhase,
  DocumentScopeSectionProjectTypes
} from "../document.types.ts"
import { DocumentScopeSectionAddMilestone } from "./DocumentScopeSectionAddMilestone.tsx"
import { getDocumentScopeSectionColumnsColumns } from "./DocumentScopeSectionColumns.tsx"
import { DocumentScopeSectionFooter } from "./DocumentScopeSectionFooter.tsx"

export interface DocumentScopeSectionListRef {
  setCollapsedActiveKey: React.Dispatch<React.SetStateAction<string[]>>
}

interface DocumentScopeSectionListProps {
  form: FormInstance<DocumentFormData>
  fields: FormListFieldData[]
  columnKey: Extract<keyof DocumentFormData, "project_phases">
  operations: FormListOperation
  projectTypes: DocumentScopeSectionProjectTypes
  listRef: Ref<DocumentScopeSectionListRef>
}

export const DocumentScopeSectionList = ({
  form,
  fields,
  columnKey,
  listRef,
  projectTypes,
  operations: { add, remove }
}: DocumentScopeSectionListProps) => {
  const typeOrderRef = useRef<string[]>([])
  const [activeKey, setActiveKey] = useState<string[]>([])

  const onAddMilestone = (
    defaultValue?: Partial<DocumentScopeSectionPhase>,
    insertIndex?: number
  ) => {
    add(defaultValue, insertIndex)

    const newIndex = insertIndex ?? fields.length
    const projectType = defaultValue?.project_type

    if (projectType && !activeKey?.includes(projectType)) {
      setActiveKey((prev) => [...(prev || []), projectType])
    }

    setTimeout(() => {
      form.focusField([
        columnKey,
        newIndex,
        "name" as keyof DocumentScopeSectionPhase
      ])
    }, 500)
  }

  const groupedFields = useMemo(() => {
    const phases = form.getFieldValue(columnKey) || []
    const groupMap = new Map<
      string,
      {
        projectType: string
        projectTypeName: string
        fields: FormListFieldData[]
      }
    >()

    fields.forEach((field) => {
      const phase = phases[field.name]
      if (!phase) return
      const typeId = phase.project_type || "unknown"
      const typeName = phase.project_type_name || "Other"

      if (!groupMap.has(typeId)) {
        groupMap.set(typeId, {
          projectType: typeId,
          projectTypeName: typeName,
          fields: []
        })
      }
      groupMap.get(typeId)!.fields.push(field)
    })

    const kept = typeOrderRef?.current?.filter((id) => groupMap.has(id))
    const newIds = [...groupMap.keys()].filter((id) => !kept.includes(id))
    const newOrder = [...kept, ...newIds]

    return { groupMap, order: newOrder }
  }, [fields, form, columnKey])

  const groups = groupedFields.order.map((id) =>
    groupedFields.groupMap.get(id)!
  )

  useEffect(() => {
    typeOrderRef.current = groupedFields.order
  }, [groupedFields.order])

  useImperativeHandle<DocumentScopeSectionListRef, DocumentScopeSectionListRef>(
    listRef,
    () => ({
      setCollapsedActiveKey: setActiveKey
    })
  )

  return (
    <>
      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        items={groups.map((group) => ({
          key: group.projectType,
          label: (
            <span className="font-semibold">
              {group.projectTypeName} ({group.fields.length})
            </span>
          ),
          children: (
            <Table
              pagination={false}
              dataSource={group?.fields}
              scroll={{ x: "max-content" }}
              rowKey="key"
              bordered
              columns={getDocumentScopeSectionColumnsColumns({
                columnKey,
                onRemove: remove
              })}
            />
          )
        }))}
      />
      <DocumentScopeSectionFooter form={form} />

      <DocumentScopeSectionAddMilestone
        onAddMilestone={onAddMilestone}
        fields={fields}
        projectTypes={projectTypes}
      />
    </>
  )
}
